import React, { useState, useRef, useEffect } from 'react';
import {
    User, Mail, Key, Eye, EyeOff, Save, Camera,
    CheckCircle, Lock, Cpu, Shield, Cog, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { fetchAdminProfile } from '../../components/AdminData';

const API_BASE_URL = 'https://api.mobilink.tech/api';

const Panel = ({ children, className = "" }) => (
    <div
        className={`rounded-2xl p-5 bg-white ${className}`}
        style={{
            border: '1px solid rgba(255,125,56,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}
    >
        {children}
    </div>
);

const Message = ({ type, message }) => {
    if (!message) return null;

    const isError = type === "error";
    const colors = isError
        ? {
            color: "#dc2626",
            backgroundColor: "rgba(239,68,68,0.08)",
            borderColor: "rgba(239,68,68,0.2)",
        }
        : {
            color: "#16a34a",
            backgroundColor: "rgba(34,197,94,0.08)",
            borderColor: "rgba(34,197,94,0.2)",
        };

    return (
        <div
            className="flex items-center gap-2 text-sm rounded-lg px-4 py-2.5 border"
            style={colors}
        >
            {isError ? (
                <AlertCircle size={16} className="shrink-0" />
            ) : (
                <CheckCircle size={16} className="shrink-0" />
            )}
            <span>{message}</span>
        </div>
    );
};

export const Profile = () => {
    const fileRef = useRef();
    const [avatar, setAvatar] = useState(null);
    const [avatarFile, setAvatarFile] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [passwords, setPasswords] = useState({ current: "", newPw: "", confirm: "" });
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);

    const [profileLoading, setProfileLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);

    const [profileMessage, setProfileMessage] = useState({ type: '', message: '' });
    const [passwordMessage, setPasswordMessage] = useState({ type: '', message: '' });
    const [imageMessage, setImageMessage] = useState({ type: '', message: '' });

    const [profileSaved, setProfileSaved] = useState(false);
    const [pwSaved, setPwSaved] = useState(false);

    const getToken = () => {
        return sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('subAdminToken');
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        const token = getToken();
        if (!token) {
            setProfileMessage({ type: 'error', message: 'No authentication token found' });
            return;
        }

        setProfileLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/super-admin/profile`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const data = response.data.data;
                setForm({
                    name: data.name || '',
                    email: data.email || '',
                    phone: data.phone || ''
                });
                if (data.image) {
                    setAvatar(data.image);
                }
                sessionStorage.setItem('adminData', JSON.stringify(data));
                setProfileMessage({ type: 'success', message: 'Profile loaded successfully' });
                setTimeout(() => setProfileMessage({ type: '', message: '' }), 3000);
            } else {
                setProfileMessage({ type: 'error', message: response.data.message || 'Failed to load profile' });
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            if (error.response?.status === 401) {
                setProfileMessage({ type: 'error', message: 'Session expired. Please login again.' });
            } else {
                setProfileMessage({ type: 'error', message: error.response?.data?.message || 'Failed to load profile' });
            }
        } finally {
            setProfileLoading(false);
        }
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarFile(file);
            setAvatar(URL.createObjectURL(file));
            handleImageUpload(file);
        }
    };

    const handleImageUpload = async (file) => {
        const token = getToken();
        if (!token) {
            setImageMessage({ type: 'error', message: 'No authentication token found' });
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        setImageLoading(true);
        setImageMessage({ type: '', message: '' });

        try {
            const response = await axios.put(
                `${API_BASE_URL}/super-admin/update-profile-image`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                setImageMessage({ type: 'success', message: 'Profile image updated successfully!' });
                const adminData = sessionStorage.getItem('adminData');
                if (adminData) {
                    const parsedData = JSON.parse(adminData);
                    parsedData.image = response.data.imageUrl || avatar;
                    sessionStorage.setItem('adminData', JSON.stringify(parsedData));
                    console.log("🚀 Dispatching adminProfileUpdated");

                    // Notify Navbar + Sidebar
                    window.dispatchEvent(new Event('adminProfileUpdated'));
                    console.log("Profile updated → Settings");
                    fetchAdminProfile();
                }
                setTimeout(() => setImageMessage({ type: '', message: '' }), 3000);
            } else {
                setImageMessage({ type: 'error', message: response.data.message || 'Failed to update image' });
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            setImageMessage({
                type: 'error',
                message: error.response?.data?.message || 'Failed to upload image'
            });
        } finally {
            setImageLoading(false);
        }
    };

    const handleProfileSave = async () => {
        const token = getToken();
        if (!token) {
            setProfileMessage({ type: 'error', message: 'No authentication token found' });
            return;
        }

        setProfileLoading(true);
        setProfileMessage({ type: '', message: '' });

        try {
            const response = await axios.put(
                `${API_BASE_URL}/super-admin/update-profile`,
                {
                    name: form.name,
                    email: form.email,
                    phone: form.phone
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setProfileSaved(true);

                setProfileMessage({
                    type: 'success',
                    message: 'Profile updated successfully!'
                });

                sessionStorage.setItem(
                    'adminData',
                    JSON.stringify(response.data.data)
                );



                setTimeout(() => {
                    setProfileSaved(false);
                    setProfileMessage({ type: '', message: '' });
                }, 2500);
            } else {
                setProfileMessage({ type: 'error', message: response.data.message || 'Failed to update profile' });
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            if (error.response?.status === 401) {
                setProfileMessage({ type: 'error', message: 'Session expired. Please login again.' });
            } else {
                setProfileMessage({ type: 'error', message: error.response?.data?.message || 'Failed to update profile' });
            }
        } finally {
            setProfileLoading(false);
        }
    };

    const handlePwSave = async () => {
        const token = getToken();
        if (!token) {
            setPasswordMessage({ type: 'error', message: 'No authentication token found' });
            return;
        }

        if (!passwords.current || !passwords.newPw || !passwords.confirm) {
            setPasswordMessage({ type: 'error', message: 'Please fill in all password fields' });
            return;
        }

        if (passwords.newPw.length < 6) {
            setPasswordMessage({ type: 'error', message: 'New password must be at least 6 characters' });
            return;
        }

        if (passwords.newPw !== passwords.confirm) {
            setPasswordMessage({ type: 'error', message: 'Passwords do not match' });
            return;
        }

        setPasswordLoading(true);
        setPasswordMessage({ type: '', message: '' });

        try {
            const response = await axios.put(
                `${API_BASE_URL}/super-admin/change-password`,
                {
                    password: passwords.current,
                    newPassword: passwords.newPw,
                    confirmPassword: passwords.confirm
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setPwSaved(true);
                setPasswordMessage({ type: 'success', message: 'Password changed successfully!' });
                setPasswords({ current: "", newPw: "", confirm: "" });
                setTimeout(() => {
                    setPwSaved(false);
                    setPasswordMessage({ type: '', message: '' });
                }, 2500);
            } else {
                setPasswordMessage({ type: 'error', message: response.data.message || 'Failed to change password' });
            }
        } catch (error) {
            console.error('Error changing password:', error);
            if (error.response?.status === 401) {
                setPasswordMessage({ type: 'error', message: 'Current password is incorrect' });
            } else {
                setPasswordMessage({ type: 'error', message: error.response?.data?.message || 'Failed to change password' });
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    return (
        <div className="space-y-6 bg-gray-50 min-h-screen p-4 rounded-2xl">
            <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div>
                    <h1 className="text-xl font-bold text-gray-800">Settings</h1>
                    <p className="text-sm mt-0.5 text-gray-500">Manage your admin profile and password</p>
                </div>
                <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-orange-50 text-orange-500 border border-orange-200">
                    <Cog size={16} />
                    <span>Admin</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Panel>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md">
                            <User size={16} className="text-white" />
                        </div>
                        <div>
                            <h2 className="font-semibold text-gray-800">Profile</h2>
                            <p className="text-xs text-gray-500">Name, email, and profile photo</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 mb-6 p-4 rounded-2xl bg-orange-50 border border-orange-200">
                        <div className="relative shrink-0">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-xl font-bold shadow-md overflow-hidden">
                                {avatar ? (
                                    <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
                                ) : (
                                    form.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "AD"
                                )}
                            </div>
                            <button
                                onClick={() => fileRef.current.click()}
                                disabled={imageLoading}
                                className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full flex items-center justify-center transition-colors bg-white border border-orange-200 text-gray-400 hover:text-orange-500 hover:border-orange-400 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {imageLoading ? (
                                    <div className="animate-spin h-3 w-3 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                                ) : (
                                    <Camera size={11} />
                                )}
                            </button>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-800">{form.name || "Admin"}</p>
                            <p className="text-xs mt-0.5 text-gray-500">{form.email}</p>
                            <button
                                onClick={() => fileRef.current.click()}
                                disabled={imageLoading}
                                className="text-xs font-medium mt-1.5 transition-colors text-orange-500 hover:text-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {imageLoading ? 'Uploading...' : 'Change photo'}
                            </button>
                        </div>
                    </div>

                    <Message type={imageMessage.type} message={imageMessage.message} />

                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium mb-1.5 text-gray-600">Full Name</label>
                            <div className="relative">
                                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                                    placeholder="Enter your full name"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1.5 text-gray-600">Email Address</label>
                            <div className="relative">
                                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                                    placeholder="Enter your email"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1.5 text-gray-600">Phone Number</label>
                            <div className="relative">
                                <Cpu size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={form.phone || ''}
                                    onChange={e => setForm({ ...form, phone: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                                    placeholder="Enter your phone number"
                                />
                            </div>
                        </div>
                    </div>

                    <Message type={profileMessage.type} message={profileMessage.message} />

                    <div className="flex justify-end mt-5">
                        <button
                            onClick={handleProfileSave}
                            disabled={profileLoading}
                            className={`flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed ${profileSaved ? "bg-gradient-to-r from-green-500 to-emerald-500" : "bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30"
                                } text-white shadow-sm`}
                        >
                            {profileLoading ? (
                                <>
                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                    Saving...
                                </>
                            ) : profileSaved ? (
                                <><CheckCircle size={15} /> Saved</>
                            ) : (
                                <><Save size={15} /> Save Changes</>
                            )}
                        </button>
                    </div>
                </Panel>

                <Panel>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md">
                            <Shield size={16} className="text-white" />
                        </div>
                        <div>
                            <h2 className="font-semibold text-gray-800">Change Password</h2>
                            <p className="text-xs text-gray-500">Update your account password</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium mb-1.5 text-gray-600">Current Password</label>
                            <div className="relative">
                                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type={showCurrent ? "text" : "password"}
                                    value={passwords.current}
                                    onChange={e => setPasswords({ ...passwords, current: e.target.value })}
                                    placeholder="Enter current password"
                                    className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                                />
                                <button
                                    onClick={() => setShowCurrent(!showCurrent)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-500 transition-colors"
                                    type="button"
                                >
                                    {showCurrent ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium mb-1.5 text-gray-600">New Password</label>
                            <div className="relative">
                                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type={showNew ? "text" : "password"}
                                    value={passwords.newPw}
                                    onChange={e => setPasswords({ ...passwords, newPw: e.target.value })}
                                    placeholder="Enter new password (min 6 chars)"
                                    className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                                />
                                <button
                                    onClick={() => setShowNew(!showNew)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-500 transition-colors"
                                    type="button"
                                >
                                    {showNew ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium mb-1.5 text-gray-600">Confirm New Password</label>
                            <div className="relative">
                                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="password"
                                    value={passwords.confirm}
                                    onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
                                    placeholder="Confirm new password"
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                                    style={{
                                        borderColor: passwords.confirm && passwords.newPw !== passwords.confirm
                                            ? '#ef4444'
                                            : undefined,
                                        color: passwords.confirm && passwords.newPw !== passwords.confirm
                                            ? '#ef4444'
                                            : undefined
                                    }}
                                />
                            </div>
                            {passwords.confirm && passwords.newPw !== passwords.confirm && (
                                <p className="text-xs mt-1 text-red-500">Passwords do not match</p>
                            )}
                            {passwords.newPw && passwords.newPw.length < 6 && (
                                <p className="text-xs mt-1 text-red-500">Password must be at least 6 characters</p>
                            )}
                        </div>
                    </div>

                    <Message type={passwordMessage.type} message={passwordMessage.message} />

                    <div className="flex justify-end mt-5">
                        <button
                            onClick={handlePwSave}
                            disabled={passwordLoading || !passwords.current || !passwords.newPw || passwords.newPw !== passwords.confirm || passwords.newPw.length < 6}
                            className={`flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed ${pwSaved ? "bg-gradient-to-r from-green-500 to-emerald-500" : "bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30"
                                } text-white shadow-sm`}
                        >
                            {passwordLoading ? (
                                <>
                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                    Updating...
                                </>
                            ) : pwSaved ? (
                                <><CheckCircle size={15} /> Updated</>
                            ) : (
                                <><Key size={15} /> Update Password</>
                            )}
                        </button>
                    </div>
                </Panel>
            </div>
        </div>
    );
};